import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  contentChildren,
  input,
  inject,
  ViewEncapsulation,
  OnDestroy,
  computed,
  ChangeDetectorRef,
  effect,
  Injector,
  runInInjectionContext,
  OutputRefSubscription
} from '@angular/core';
import { SelectionChangeEvent } from '../../domain/selectable.domain';
import { ValueAccessorProviderFactory } from '../value-accessor/value-accessor-provider-factory';
import { IdService } from '../../services/id.service';
import { RadioComponent, TD_WB_RADIO } from './radio.component';
import type { InputSignal, Signal } from '@angular/core';
import type { ControlValueAccessor } from '@angular/forms';
import type { Direction } from '../../domain/direction';

@Component({
  selector: 'td-wb-radio-group',
  templateUrl: 'radio-group.component.html',
  styleUrls: ['radio-group.component.scss'],
  providers: [ValueAccessorProviderFactory(RadioGroupComponent)],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RadioGroupComponent implements ControlValueAccessor, OnDestroy, AfterContentInit {
  private static readonly ID_PREFIX: string = 'td-wb-radio';

  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly idService: IdService = inject(IdService);
  private readonly injector: Injector = inject(Injector);

  // Inputs
  readonly name: InputSignal<string | undefined> = input<string | undefined>();
  readonly label: InputSignal<string | undefined> = input<string | undefined>();
  readonly ariaLabel: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'aria-label' });
  readonly ariaLabelledBy: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'aria-labelledby' });
  readonly direction: InputSignal<Direction> = input<Direction>('horizontal');

  // Children
  readonly radios: Signal<ReadonlyArray<RadioComponent>> = contentChildren(TD_WB_RADIO, { descendants: true });

  //Derived
  readonly isVertical: Signal<boolean> = computed((): boolean => this.direction() === 'vertical');

  // CVA
  private value: unknown = undefined;
  private onChangeCallback: (value: unknown) => void = (): void => {};
  private onTouchCallback: () => void = (): void => {};

  // Subscription lifecycle
  private radioSubs: Array<OutputRefSubscription> = [];

  ngAfterContentInit(): void {
    runInInjectionContext(this.injector, () => {
      effect((): void => {
        const radios: ReadonlyArray<RadioComponent> = this.radios();

        this.radioSubs.forEach((s: OutputRefSubscription) => s.unsubscribe());
        this.radioSubs = [];

        if (radios.length === 0){
          return;
        }

        this.updateRadioButtonRadioIds(radios);
        this.updateRadioButtonNames(radios);
        this.updateSelectedRadioFromValue(radios);

        this.radioSubs = radios.map((r: RadioComponent): OutputRefSubscription =>
          r.onSelectionChange.subscribe((event: SelectionChangeEvent<RadioComponent>): void => {
            this.value = event.source.value();

            this.updateSelectedRadioFromValue(this.radios());

            if (event.fromView) {
              this.onTouchCallback();
              this.onChangeCallback(this.value);
            }
            this.cdr.markForCheck();
          })
        );
        this.cdr.markForCheck();
      });
    });
  }

  // CVA
  writeValue(value: unknown): void {
    this.value = value;
    this.updateSelectedRadioFromValue(this.radios());
    this.cdr.markForCheck();
  }

  registerOnChange(onChangeCallback: (value: unknown) => void): void {
    this.onChangeCallback = onChangeCallback;
  }

  registerOnTouched(onTouchedCallback: () => void): void {
    this.onTouchCallback = onTouchedCallback;
  }

  setDisabledState(isDisabled: boolean): void {
    this.radios().forEach((r: RadioComponent) => r.disabled.set(isDisabled));
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.radioSubs.forEach((s: OutputRefSubscription) => s.unsubscribe());
    this.radioSubs = [];
  }

  private readonly generateGroupName: string = this.getRadioId();

  private updateRadioButtonNames(radios: ReadonlyArray<RadioComponent>): void {
    const explicit: string | undefined = this.name();
    const effective: string = (explicit !== undefined && explicit !== '') ? explicit : this.generateGroupName;
    radios.forEach((radio: RadioComponent) =>{
      radio.setNameFromGroup(effective);
      radio.markForCheck();
    });
  }

  private updateRadioButtonRadioIds(radios: ReadonlyArray<RadioComponent>): void {
    radios.forEach((radio: RadioComponent) => {
      const rid: string | undefined = radio.finalRadioId();
      if (rid === undefined || rid === '') {
        radio.setRadioIdFromGroup(this.getRadioId());
      }
      radio.markForCheck();

    });
  }

  private getRadioId(): string {
    return this.idService.generateNamespaceSequentialId(RadioGroupComponent.ID_PREFIX);
  }

  private updateSelectedRadioFromValue(radios: ReadonlyArray<RadioComponent>): void {
    const currentValue: unknown = this.value;

    radios.forEach((radio: RadioComponent): void => {
      radio.setCheckedFromGroup(currentValue === radio.value());
    });

    this.cdr.markForCheck();
  }
}